import {Show} from 'platform/foundation';
import styled from 'styled-components';
import {match} from 'ts-pattern';

import {FocusEvent, KeyboardEvent, useEffect, useRef, useState} from 'react';

import {always} from 'ramda';
import {isNotNil, isPositive} from 'ramda-adjunct';

import {suffixTestId, TestIdProps} from 'shared/web';

import {IconButton} from '../IconButton/IconButton';
import {InputSegment} from './components/InputSegment';
import {PaginationPageGroup} from './components/PaginationPageGroup';
import {TextOnlySegment} from './components/TextOnlySegment';
import {DEFAULT_PAGE} from './constants/defaultPage';
import {PaginationVariant} from './types/PaginationVariant';
import {getPageNumbers} from './utils/getPageNumbers';

export interface PaginationProps extends TestIdProps {
  pagesQuantity: number;
  onPageChange: (page: number) => void;
  showFirstButton?: boolean;
  showPrevButton?: boolean;
  showNextButton?: boolean;
  showLastButton?: boolean;
  isDisabled?: boolean;
  defaultPage?: number;
  page?: number;
  showFirstPage?: boolean;
  showLastPage?: boolean;
  variant?: PaginationVariant;
  /**
   * @about
   * The siblingCount is number value, which says how many pages will be shown around activePage.
   */
  siblingCount?: number;
  /**
   * @about
   * The boundaryCount is number value, which says how many pages will be shown near first and last page.
   */
  boundaryCount?: number;
}

type FocusTarget =
  | {type: 'first'}
  | {type: 'prev'}
  | {type: 'next'}
  | {type: 'last'}
  | {type: 'page'; page: number};

export function Pagination(props: PaginationProps) {
  const [activePage, setActivePage] = useState<number>(
    props.page ?? props.defaultPage ?? DEFAULT_PAGE
  );
  const [focusTarget, setFocusTarget] = useState<FocusTarget | null>(null);
  const [isKeyboardNavigation, setIsKeyboardNavigation] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isNotNil(props.page)) {
      // eslint-disable-next-line react/react-compiler
      setActivePage(props.page);
    }
  }, [props.page]);

  const setFirstPage = () => {
    if (!isPositive(props.page)) {
      setActivePage(1);
    }
    props.onPageChange(1);
  };

  const setPrevPage = () => {
    if (activePage === 1) {
      return;
    }
    if (!isPositive(props.page)) {
      setActivePage(activePage - 1);
    }
    props.onPageChange(activePage - 1);
  };

  const setNextPage = () => {
    if (activePage === props.pagesQuantity) {
      return;
    }
    if (!isPositive(props.page)) {
      setActivePage(activePage + 1);
    }
    props.onPageChange(activePage + 1);
  };

  const setLastPage = () => {
    if (!isPositive(props.page)) {
      setActivePage(props.pagesQuantity);
    }
    props.onPageChange(props.pagesQuantity);
  };

  const getFocusableElements = () => {
    const elements: Array<{type: 'button'; action: () => void} | {type: 'page'; page: number}> = [];

    if (props.showFirstButton) {
      elements.push({type: 'button', action: setFirstPage});
    }
    if (props.showPrevButton) {
      elements.push({type: 'button', action: setPrevPage});
    }

    if (!props.variant || props.variant === 'button') {
      const pageNumbers = getPageNumbers({
        activePage: activePage,
        siblingCount: props.siblingCount,
        boundaryCount: props.boundaryCount,
        pagesQuantity: props.pagesQuantity,
        showFirstPage: props.showFirstPage,
        showLastPage: props.showLastPage,
      });

      pageNumbers.forEach((pageNumber: number) => {
        if (pageNumber !== 0) {
          elements.push({type: 'page', page: pageNumber});
        }
      });
    }

    if (props.showNextButton) {
      elements.push({type: 'button', action: setNextPage});
    }
    if (props.showLastButton) {
      elements.push({type: 'button', action: setLastPage});
    }

    return elements;
  };

  const focusableElements = getFocusableElements();

  const activeIndex = (() => {
    if (!focusTarget) {
      return -1;
    }

    if (focusTarget.type === 'page') {
      return focusableElements.findIndex(
        (el) => el.type === 'page' && el.page === focusTarget.page
      );
    }

    return focusableElements.findIndex((el) => {
      if (el.type !== 'button') {
        return false;
      }
      return match(focusTarget.type)
        .with('first', () => el.action === setFirstPage)
        .with('prev', () => el.action === setPrevPage)
        .with('next', () => el.action === setNextPage)
        .with('last', () => el.action === setLastPage)
        .exhaustive();
    });
  })();

  const enabledIndices = focusableElements
    .map((_, index) => (props.isDisabled ? null : index))
    .filter((index): index is number => index !== null);

  const setFocusAtIndex = (index: number) => {
    const element = focusableElements[index];
    if (!element) {
      setFocusTarget(null);
      return;
    }

    if (element.type === 'page') {
      setFocusTarget({type: 'page', page: element.page});
      return;
    }

    if (element.action === setFirstPage) {
      setFocusTarget({type: 'first'});
    } else if (element.action === setPrevPage) {
      setFocusTarget({type: 'prev'});
    } else if (element.action === setNextPage) {
      setFocusTarget({type: 'next'});
    } else if (element.action === setLastPage) {
      setFocusTarget({type: 'last'});
    }
  };

  const changePage = (page: number) => {
    if (!isPositive(props.page)) {
      setActivePage(page);
    }

    setFocusTarget({type: 'page', page});
    props.onPageChange(page);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (
      props.isDisabled ||
      enabledIndices.length === 0 ||
      props.variant === 'input' ||
      props.variant === 'text-only'
    ) {
      return;
    }

    if (document.activeElement !== wrapperRef.current && wrapperRef.current) {
      wrapperRef.current.focus();
    }

    const currentPosition = enabledIndices.indexOf(activeIndex);

    match(e.key)
      .with('ArrowRight', 'ArrowDown', () => {
        e.preventDefault();
        if (activeIndex === -1) {
          setFocusAtIndex(enabledIndices[0]);
        } else if (currentPosition === enabledIndices.length - 1) {
          setFocusAtIndex(enabledIndices[0]);
        } else {
          setFocusAtIndex(enabledIndices[currentPosition + 1]);
        }
      })
      .with('ArrowLeft', 'ArrowUp', () => {
        e.preventDefault();
        if (activeIndex === -1) {
          setFocusAtIndex(enabledIndices[enabledIndices.length - 1]);
        } else if (currentPosition === 0) {
          setFocusAtIndex(enabledIndices[enabledIndices.length - 1]);
        } else {
          setFocusAtIndex(enabledIndices[currentPosition - 1]);
        }
      })
      .with(' ', 'Enter', () => {
        e.preventDefault();
        if (activeIndex >= 0 && activeIndex < focusableElements.length) {
          const element = focusableElements[activeIndex];
          if (element.type === 'button') {
            element.action();
          } else if (element.type === 'page') {
            changePage(element.page);
          }
        }
      })
      .with('Home', () => {
        e.preventDefault();
        setFocusAtIndex(enabledIndices[0]);
      })
      .with('End', () => {
        e.preventDefault();
        setFocusAtIndex(enabledIndices[enabledIndices.length - 1]);
      })
      .otherwise(() => {});
  };

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!wrapperRef.current?.contains(event.relatedTarget as Node)) {
      setFocusTarget(null);
    }
  };

  const handleFocus = (event: FocusEvent<HTMLDivElement>) => {
    let isKeyboardNavigationActive = false;
    if (event.target?.matches) {
      isKeyboardNavigationActive = event.target.matches(':focus-visible');
    }

    setIsKeyboardNavigation(isKeyboardNavigationActive);

    if (isKeyboardNavigationActive && activeIndex === -1 && enabledIndices.length > 0) {
      setFocusAtIndex(enabledIndices[0]);
    }
  };

  const getElementIndex = (elementType: 'first' | 'prev' | 'next' | 'last') => {
    return focusableElements.findIndex((el) => {
      if (el.type !== 'button') {
        return false;
      }
      return match(elementType)
        .with('first', () => el.action === setFirstPage)
        .with('prev', () => el.action === setPrevPage)
        .with('next', () => el.action === setNextPage)
        .with('last', () => el.action === setLastPage)
        .exhaustive();
    });
  };

  const firstButtonIndex = getElementIndex('first');
  const prevButtonIndex = getElementIndex('prev');
  const nextButtonIndex = getElementIndex('next');
  const lastButtonIndex = getElementIndex('last');

  return (
    <StyledWrapper
      ref={wrapperRef}
      tabIndex={props.isDisabled ? -1 : 0}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      onFocus={handleFocus}
      data-testid={suffixTestId('paginationWrapper', props)}
    >
      <Show when={props.showFirstButton}>
        <IconButton
          size="small"
          priority="secondary"
          icon="navigation/first_page"
          onClick={() => {
            setFirstPage();
            setFocusTarget({type: 'first'});
          }}
          isDisabled={props.isDisabled}
          isFocused={firstButtonIndex === activeIndex}
          tabIndex={-1}
          data-testid={suffixTestId('paginationFirstButton', props)}
        />
      </Show>
      <Show when={props.showPrevButton}>
        <IconButton
          size="small"
          priority="secondary"
          icon="hardware/keyboard_arrow_left"
          onClick={() => {
            setPrevPage();
            setFocusTarget({type: 'prev'});
          }}
          isDisabled={props.isDisabled}
          isFocused={prevButtonIndex === activeIndex}
          tabIndex={-1}
          data-testid={suffixTestId('paginationPrevButton', props)}
        />
      </Show>
      {match(props.variant)
        .with(
          'input',
          always(
            <InputSegment
              changePage={changePage}
              activePage={activePage}
              pagesQuantity={props.pagesQuantity}
              isDisabled={props.isDisabled}
              data-testid={suffixTestId('paginationInput', props)}
            />
          )
        )
        .with(
          'text-only',
          always(
            <TextOnlySegment
              activePage={activePage}
              pagesQuantity={props.pagesQuantity}
              data-testid={suffixTestId('paginationTextOnly', props)}
            />
          )
        )
        .otherwise(
          always(
            <PaginationPageGroup
              activePage={activePage}
              changePage={changePage}
              isDisabled={props.isDisabled}
              siblingCount={props.siblingCount}
              boundaryCount={props.boundaryCount}
              pagesQuantity={props.pagesQuantity}
              showFirstPage={props.showFirstPage}
              showLastPage={props.showLastPage}
              activeIndex={activeIndex}
              isKeyboardNavigation={isKeyboardNavigation}
              getFocusableElements={getFocusableElements}
              data-testid={suffixTestId('paginationPageGroup', props)}
            />
          )
        )}
      <Show when={props.showNextButton}>
        <IconButton
          size="small"
          priority="secondary"
          icon="hardware/keyboard_arrow_right"
          onClick={() => {
            setNextPage();
            setFocusTarget({type: 'next'});
          }}
          isDisabled={props.isDisabled}
          isFocused={nextButtonIndex === activeIndex}
          tabIndex={-1}
          data-testid={suffixTestId('paginationNextButton', props)}
        />
      </Show>
      <Show when={props.showLastButton}>
        <IconButton
          size="small"
          priority="secondary"
          icon="navigation/last_page"
          onClick={() => {
            setLastPage();
            setFocusTarget({type: 'last'});
          }}
          isDisabled={props.isDisabled}
          isFocused={lastButtonIndex === activeIndex}
          tabIndex={-1}
          data-testid={suffixTestId('paginationLastButton', props)}
        />
      </Show>
    </StyledWrapper>
  );
}

const StyledWrapper = styled.div`
  height: 32px;
  display: flex;
  align-items: center;

  > *:not(style) ~ *:not(style) {
    margin-left: 4px;
  }

  &:focus {
    outline: none;
  }
`;
